/* eslint-disable react/no-unstable-nested-components */
import type { ComponentProps, FC, LazyExoticComponent, ReactElement } from "react"

import type { QueryKey } from "@tanstack/react-query"
import { ErrorBoundary } from "react-error-boundary"
import { Navigate } from "react-router-dom"

import { EWaveLoaderVariant, ErrorHint, Loadable, WaveLoader } from "@/3514/components"
import { type TUserState, useUser } from "@/3514/hooks"
import { CoachLayout } from "@/3514/layouts"
import { isUserCoach } from "@/3514/utils"
import { INDEX_URL } from "@/constants"

type _TProps = { container: LazyExoticComponent<FC>; queryKeys: QueryKey[] } & Pick<
    ComponentProps<typeof CoachLayout>,
    "withBackButton"
>

const classes = { loader: (): string => "relative top-[100px] -translate-y-[0px]" }

const CoachCalendarPageSkeleton: FC<_TProps> = ({
    container: Container,
    queryKeys,
    withBackButton = false
}: _TProps): ReactElement => {
    const { user }: TUserState = useUser()

    const isCoach: boolean = isUserCoach(user)

    return isCoach ? (
        <CoachLayout withBackButton={withBackButton}>
            <ErrorBoundary
                resetKeys={queryKeys}
                fallback={<ErrorHint isVisible containerClassName="mx-auto mt-[50px]" />}
            >
                <Loadable
                    withEnforcedSuspense
                    component={Container}
                    loadingFallback={
                        <WaveLoader
                            variant={EWaveLoaderVariant.DarkBlue}
                            wrapperClassName={classes.loader()}
                            cssIdentifier="ccps-wl"
                        />
                    }
                />
            </ErrorBoundary>
        </CoachLayout>
    ) : (
        <Navigate to={INDEX_URL} replace />
    )
}

CoachCalendarPageSkeleton.displayName = "CoachCalendarPageSkeleton"

export default CoachCalendarPageSkeleton
